import React, { useCallback, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { useFormikContext } from 'formik';
import { lookupsJson } from '@ourbranch/lookups';

import { LabelTooltip } from 'core/components/label-tooltip';
import Section from 'core/components/section';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useToast } from 'core/components/toast';
import useStyles from './policy-wide-endorsements.styles';

const identityFraudLimitOptions = [
  { id: '0', value: 'No coverage' },
  { id: '15000', value: '$15,000' }
];

const { fungiLiabilityOptions, fungiPropertyOptions } = lookupsJson.farm;

const PolicyWideEndorsements = () => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { disableForm: disableRiskForm, setActiveForm } = useActiveFormState('PolicyWideEndorsements');
  const toast = useToast();

  const setPolicyWideEndorsementsToActive = useCallback(() => {
    setActiveForm('PolicyWideEndorsements');
  }, [setActiveForm]);

  const allowEditIdentityFraudExpenseCoverage =
    values.farmLocations?.some((location) =>
      location.risks?.some(
        (risk) => ['FO1', 'FO2', 'FO3', 'FO4'].includes(risk.type) && risk.building?.residenceUsage === 'P'
      )
    ) ?? false;

  useEffect(() => {
    if (!allowEditIdentityFraudExpenseCoverage && values.farmCoverage?.identityFraudLimit !== 0) {
      setFieldValue('farmCoverage.identityFraudLimit', 0);
      toast.notify({
        type: 'info',
        message: 'Identity fraud coverage has also been updated to No coverage to match.'
      });
    }
  }, [allowEditIdentityFraudExpenseCoverage, values.farmCoverage?.identityFraudLimit, setFieldValue, toast]);

  const allowEditFo6OnlyCoverage =
    values.farmLocations?.some((location) =>
      location.risks?.some((risk) => ['FO6Scheduled', 'FO6Structures', 'FO6Unscheduled'].includes(risk.type))
    ) ?? false;

  useEffect(() => {
    if (!allowEditFo6OnlyCoverage && values.farmCoverage?.extraExpenseLimit !== 0) {
      setFieldValue('farmCoverage.extraExpenseLimit', 0);
      toast.notify({
        type: 'info',
        message: 'Farm extra expense coverage has also been updated to $0 (No coverage) to match.'
      });
    }
    if (!allowEditFo6OnlyCoverage && values.farmCoverage?.hasEquipmentBreakdownCoverage) {
      setFieldValue('farmCoverage.hasEquipmentBreakdownCoverage', false);
      toast.notify({
        type: 'info',
        message: 'Farm equipment breakdown coverage has been de-selected to match.'
      });
    }
  }, [
    allowEditFo6OnlyCoverage,
    values.farmCoverage?.extraExpenseLimit,
    values.farmCoverage?.hasEquipmentBreakdownCoverage,
    setFieldValue,
    toast
  ]);

  return (
    <Section title={'Policy-wide endorsements'} type="SubSection">
      <Grid container className={classes.endorsementsContainer} alignItems="flex-start">
        <Label className={classes.marginBottom} type="formSubTitle">
          Optional Coverages
        </Label>

        <Grid container spacing={2}>
          {allowEditIdentityFraudExpenseCoverage ? (
            <Field
              name="farmCoverage.identityFraudLimit"
              id="farmCoverage.identityFraudLimit"
              type="select"
              label="Identity fraud expense coverage"
              mode="dark"
              options={identityFraudLimitOptions}
              xs={6}
              disabled={disableRiskForm}
              onChange={setPolicyWideEndorsementsToActive}
            />
          ) : (
            <Field
              name="farmCoverage.identityFraudLimit"
              id="farmCoverage.identityFraudLimit"
              type="value"
              label="Identity fraud expense coverage"
              mode="dark"
              value={'No coverage'}
              xs={6}
              disabled={disableRiskForm}
              lockedValue={{
                text: 'Identity fraud coverage can only be edited if there are dwelling risks on the policy that have been indicated to be the primary residence of the insured.'
              }}
            />
          )}
          <Field
            className={classes.checkboxContainer}
            name="farmCoverage.hasEquipmentBreakdownCoverage"
            id="farmCoverage.hasEquipmentBreakdownCoverage"
            type="checkbox"
            label="Equipment breakdown"
            mode="dark"
            xs={6}
            disabled={disableRiskForm || !allowEditFo6OnlyCoverage}
            onChange={setPolicyWideEndorsementsToActive}
          />
          {allowEditFo6OnlyCoverage ? (
            <Field
              name="farmCoverage.extraExpenseLimit"
              id="farmCoverage.extraExpenseLimit"
              type="string"
              label="farm extra expense coverage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              onChange={setPolicyWideEndorsementsToActive}
            />
          ) : (
            <Field
              name="farmCoverage.extraExpenseLimit"
              id="farmCoverage.extraExpenseLimit"
              type="value"
              label="farm extra expense coverage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              value={'$0 (No coverage)'}
              lockedValue={{
                text: 'Farm extra expense coverage can only be edited if there are FO-6 risks on the policy'
              }}
            />
          )}
          <Label className={classes.header} type="formSubTitle">
            Limited fungi, wet or dry rot, or bacteria coverage
          </Label>
          <Grid item xs={6}>
            <LabelTooltip
              label="Property coverage limit"
              tooltip={{
                label: 'More Info',
                onHoverText:
                  'Included when Coverage A is present. Or when Coverage E, F, or G is selected with liability coverage. Not included if Coverage C is the only coverage on the policy.'
              }}
            >
              <Field
                name="farmCoverage.fungiPropertyLimit"
                id="farmCoverage.fungiPropertyLimit"
                type="select"
                mode="dark"
                options={fungiPropertyOptions}
                disabled={true}
              />
            </LabelTooltip>
          </Grid>
          <Grid item xs={6}>
            <LabelTooltip
              label="Liability coverage limit"
              tooltip={{
                label: 'More Info',
                onHoverText:
                  'Included when Coverage A or C is present. Or when Coverage E, F, or G is selected with liability coverage. Not included if liability coverage is not selected.'
              }}
            >
              <Field
                name="farmCoverage.fungiLiabilityLimit"
                id="farmCoverage.fungiLiabilityLimit"
                type="select"
                mode="dark"
                options={fungiLiabilityOptions}
                disabled={true}
              />
            </LabelTooltip>
          </Grid>
        </Grid>
      </Grid>
    </Section>
  );
};

export default PolicyWideEndorsements;
